import React, { useState, useEffect } from "react";
import { 
  MDBRow, 
  MDBCard, 
  MDBCardBody, 
  MDBCardHeader, 
  MDBCol, 
  MDBBtn,
  MDBInput,
  MDBIcon,
  MDBTooltip
} from "mdb-react-ui-kit";
import CommonViewLayout from "../CommonViewLayout";
import Editor from "../../../../Components/Editor";
import { SuccessAlert, ErrorAlert } from '../../../../Components/Alert';
import useAxiosCall from '../../../../Hooks/useAxiosCall';
import MessageValidator from '../../../../Components/MessageValidator';

const FieldWrapper = ({ children }) => (
  <div className="mb-3">
    {children}
  </div>
);

const ComposeMessage = ({ onBack, editingMessage = null }) => {
  const [content, setContent] = useState(editingMessage?.content || "");
  const [dateFrom, setDateFrom] = useState(editingMessage?.dateFrom || "");
  const [dateTo, setDateTo] = useState(editingMessage?.dateTo || "");
  const [alert, setAlert] = useState({ type: '', message: '' });
  const { makeApiCall } = useAxiosCall();
  const [updated, setUpdated] = useState(false);
  const validator = MessageValidator();
  const [isGlobalMessage, setIsGlobalMessage] = useState(editingMessage?.isGlobal || false);

  useEffect(() => {
    validator.showMessages();
  }, [updated]);

  // Handle content change
  const handleContentChange = (newContent) => {
    // Ensure we're getting the actual content from the editor
    const editorContent = typeof newContent === 'string' ? newContent : newContent?.html || '';
    setContent(editorContent);
    
    // Validate immediately after content change
    validator.message('content', editorContent, 'messageContent');
    setUpdated(!updated);
  };

  // Handle date from change
  const handleDateFromChange = (e) => {
    const newDateFrom = e.target.value;
    setDateFrom(newDateFrom);
    
    // If dateTo is before new dateFrom, reset dateTo
    if (dateTo && new Date(dateTo) <= new Date(newDateFrom)) {
      setDateTo('');
    }
  };

  // Get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const handleApiCallSuccess = (data) => {
    setAlert({ type: 'success', message: data.message });
    setTimeout(() => {
      onBack();
    }, 1500);
  };

  const handleApiCallError = (errorMessage) => {
    setAlert({ type: 'error', message: errorMessage });
  };

  const handleSave = (event) => {
    event.preventDefault();
    
    // Force validation check before submission
    validator.message('content', content, 'messageContent');
    validator.message('dateFrom', dateFrom, 'dateFrom');
    validator.message('dateTo', dateTo, ['dateTo', 'dateRange:' + dateFrom]);
    
    if (validator.allValid()) {
      const requestData = {
        content,
        dateFrom,
        dateTo,
        isGlobal: isGlobalMessage
      };

      makeApiCall({
        requestData,
        onSuccess: handleApiCallSuccess,
        onError: handleApiCallError,
        requestType: 'POST',
        url: editingMessage ? "updateMessage" : "createMessage",
        loader: true
      });
    } else {
      validator.showMessages();
      setUpdated(!updated);
      setAlert({ type: 'error', message: 'Please fill all required fields correctly.' });
    }
  };

  return (
    <CommonViewLayout
      headerTitle={editingMessage ? "Edit Message" : "Compose Message"}
      iconName="edit"
      showBackButton={true}
    >
      <section>
        <MDBRow>
          <MDBCol>
            <MDBCard>
              <MDBCardHeader>
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">
                    {/* {editingMessage ? "Edit Message" : "New Message"} */}
                  </h5>
                  <MDBBtn 
                    color="secondary" 
                    size="sm" 
                    onClick={onBack}
                  >
                    Back to Messages
                  </MDBBtn>
                </div>
              </MDBCardHeader>
              <MDBCardBody>
                {alert.type === 'success' && <SuccessAlert success={true} messages={[alert.message]} resetAlert={setAlert} />}
                {alert.type === 'error' && <ErrorAlert error={true} messages={[alert.message]} resetAlert={setAlert} />}
                
                <MDBRow className="mb-3">
                  <MDBCol md="6" className="mb-3 mb-md-0">
                    <FieldWrapper>
                      <MDBInput
                        type="date"
                        label="Date From"
                        value={dateFrom}
                        onChange={handleDateFromChange}
                        min={getTodayDate()}
                        required
                      />
                      <div className="small">
                        {validator.message('dateFrom', dateFrom, 'dateFrom')}
                      </div>
                    </FieldWrapper>
                  </MDBCol>
                  <MDBCol md="6">
                    <FieldWrapper>
                      <MDBInput
                        type="date"
                        label="Date To"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                        min={dateFrom || getTodayDate()}
                        disabled={!dateFrom}
                        required
                      />
                      <div className="small">
                        {validator.message('dateTo', dateTo, ['dateTo', 'dateRange:' + dateFrom])}
                      </div>
                    </FieldWrapper>
                  </MDBCol>
                </MDBRow>
                <MDBRow className="mb-3">
                  <MDBCol>
                    <FieldWrapper>
                      <div className="form-check mb-3">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="allDomainsCheck"
                          checked={isGlobalMessage}
                          onChange={(e) => setIsGlobalMessage(e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="allDomainsCheck">
                          Send to all domains { '  '}
                          <MDBTooltip tag='span' className="ms-1" title="This message will be visible to users across all domains">
                            <MDBIcon fas icon="info-circle" />
                          </MDBTooltip>
                        </label>
                      </div>
                    </FieldWrapper>
                  </MDBCol>
                </MDBRow>
                <MDBRow className="mb-3">
                  <MDBCol>
                    <FieldWrapper>
                      <label className="form-label">Message Content <span className="text-danger">*</span></label>
                      <div className="editor-container" style={{ border: '1px solid #ced4da', borderRadius: '0.25rem' }}>
                        <Editor 
                          value={content}
                          onChange={handleContentChange}
                        />
                      </div>
                      <div className="small mt-1">
                        {validator.message('content', content, 'messageContent')}
                      </div>
                    </FieldWrapper>
                  </MDBCol>
                </MDBRow>
                <MDBRow>
                  <MDBCol className="d-flex justify-content-end gap-2">
                    <MDBBtn color="secondary" onClick={onBack}>
                      Cancel
                    </MDBBtn>
                    <MDBBtn color="primary" onClick={handleSave}>
                      {editingMessage ? "Update" : "Send"} Message
                    </MDBBtn>
                  </MDBCol>
                </MDBRow>
              </MDBCardBody>
            </MDBCard>
          </MDBCol>
        </MDBRow>
      </section>
    </CommonViewLayout>
  );
};

export default ComposeMessage;